import React, { useEffect, useCallback } from "react"
import PropTypes from "prop-types" // Import PropTypes
import { Layout, Input, List, Typography, theme } from "antd"
import { SearchOutlined, CloseCircleOutlined } from "@ant-design/icons"
import { useSearchParams } from "react-router-dom"
import { Conversation } from "../Conversation"
import { sumBy, debounce } from "lodash"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { getConversations, setViewedMessagesCount, getSearchConversation } from "store/conversations"
import { collectionFirstSelector, metadataSelector } from "store/selectors"
import { firstLoadingSelector } from "modules/loader-watchers/selectors"
import styles from "./ConversationList.module.css"
import classNames from "classnames"
import { NavigationPath } from "layoutsV2/pro/ProHomeLayout/NavigationPath"
import { GroupMessages } from "ui/Icon/DSIcon/GroupMessages"
import { renderEmptyStateCard } from "pagesV2/pro/Messages/ChatConversation"
import Spinner from "components/common/Spinner"
const { Sider } = Layout
const { Text } = Typography
const { useToken } = theme

export const ConversationList = ({ conversations, selectedConversationId, isSmallDevice, setInputValue, inputValue }) => {
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const { token } = useToken()
  const [page, setPage] = React.useState(1)
  const [loading, setLoading] = React.useState(true)
  const firstLoading = useSelector(firstLoadingSelector("conversations"))
  const conversationsFirst = useSelector(collectionFirstSelector("conversations"))
  const conversationsFirstId = conversationsFirst?.id
  const metadata = useSelector(metadataSelector("conversations"))
  const isLoading = metadata?.loading
  const hasMore = metadata.page < metadata.total_pages
  const perPageLimit = 10
  const showNoResult = inputValue && conversations.length === 0
  const user = useSelector((state) => state.user)
  const isGuide = user.role === "guide_user"
  const filteredConversationsById = conversations?.filter((convresation) => convresation.id === selectedConversationId)
  const unviewed = sumBy(conversations, "unviewed")
  let totalUnviewed = null

  if (unviewed > 0 && filteredConversationsById.length > 0) {
    totalUnviewed = unviewed - Number(filteredConversationsById.at(0).unviewed)
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSearch(value)
  }

  const handleClearInput = () => {
    setInputValue("")
    debouncedSearch("")
  }

  const handleScroll = useCallback(
    debounce((e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      const nearBottom = scrollHeight - scrollTop <= clientHeight + 50
      if (nearBottom && !isLoading && hasMore) {
        const nextPage = page + 1
        dispatch(getConversations(null, { page: nextPage, per_page: perPageLimit, append: true }))
        setPage(nextPage) // increment page
      }
    }, 200),
    [page, isLoading, hasMore]
  )

  useEffect(() => {
    if (!selectedConversationId && conversationsFirstId) searchParams.set("conversation_id", conversationsFirstId)
  }, [selectedConversationId, conversationsFirstId]) //eslint-disable-line

  useEffect(() => {
    const fetchInitialConversations = async () => {
      if (firstLoading) {
        setLoading(true) // Set loading to true before fetching data
        try {
          await dispatch(getConversations(null, { page: 1, per_page: perPageLimit, append: false }))
        } catch (error) {
          console.error("Error fetching initial conversations:", error)
        } finally {
          setTimeout(() => {
            setLoading(false) // Set loading to false after 2 seconds
          }, 2000)
        }
      }
    }

    fetchInitialConversations()
  }, [firstLoading]) //eslint-disable-line

  useEffect(() => {
    dispatch(setViewedMessagesCount(totalUnviewed))
  }, [totalUnviewed]) //eslint-disable-line

  const debouncedSearch = useCallback(
    debounce((value) => {
      let param = {}

      if (value) {
        if (isGuide) {
          param = { "q[user_full_name_cont]": value }
        } else {
          param = { "q[guide_user_full_name_cont]": value }
        }
      }
      dispatch(getSearchConversation(null, param))
    }, 300),
    [isGuide, dispatch]
  )

  const hasConversations = conversations.length > 0

  const renderEmptyState = () => {
    if (isSmallDevice) {
      return renderEmptyStateCard(GroupMessages, t("messenger.empty_messages_title"), t("messenger.start_conversation"), token.colorPrimary)
    } else if (showNoResult) {
      return (
        <div className={styles.noDataFound}>
          <Text>{t("global.no_result_found")}</Text>
        </div>
      )
    } else if (loading) {
      return <Spinner className="flex-fill" />
    }
  }

  return (
    <Sider width={isSmallDevice ? "100%" : 428} className={classNames(isSmallDevice && styles.smallDevice, styles.sider)}>
      {isSmallDevice && <NavigationPath className="pl-m pr-m" />}
      <div className={styles.searchBox}>
        <Input
          className={styles.searchInput}
          placeholder="Search"
          value={inputValue}
          prefix={<SearchOutlined />}
          onChange={handleSearchChange}
          suffix={
            inputValue && <CloseCircleOutlined style={{ color: "rgba(0, 0, 0, 0.45)", cursor: "pointer" }} onClick={handleClearInput} />
          }
        />
      </div>
      {hasConversations ? (
        <List
          className={styles.list}
          style={{
            height: "calc(100vh - 240px)", // adjust to fit layout
            overflowY: "auto"
          }}
          onScroll={handleScroll}
          dataSource={conversations}
          renderItem={(item) => <Conversation key={item.id} conversation={item} />}
        />
      ) : (
        renderEmptyState()
      )}
    </Sider>
  )
}

// Add PropTypes validation
ConversationList.propTypes = {
  conversations: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      unviewed: PropTypes.number
    })
  ).isRequired,
  selectedConversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isSmallDevice: PropTypes.bool.isRequired,
  setInputValue: PropTypes.func.isRequired,
  inputValue: PropTypes.string.isRequired
}
